import { Alert, Box } from '@mui/material';
import { isWithinInterval } from 'date-fns';
import { filter, find, map, property, some } from 'lodash';
import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useMixpanel } from 'react-mixpanel-browser';

import { AudioClip, getAudioClipUrl, useAudioClips } from '@/api/audioClips';
import { SoundPressureReading, useSoundPressureReadings } from '@/api/historicalData';
import { usePreferredTimeZone } from '@/api/userAccount';
import { Spectrogram, drawSpectrogram } from '@/charts/spectrogramChart';
import { LoadingIndicatorIcon } from '@/components/loaders/LoadingIndicatorIcon';
import {
    AUDIO_CLIP_LANE_HEIGHT_PX,
    AudioClipLane,
    AudioClipLaneProps,
    ClipPlayback,
    ClipPlaybackPhase,
} from '@/components/spotter/historicalDataTab/AudioClipLane';
import { AudioClipSaveButtons } from '@/components/spotter/historicalDataTab/AudioClipSaveButtons';
import {
    CHART_DETAIL_PANEL_WIDTH,
    ChartDataDetails,
    ChartDataSelectors,
    ChartWithInfo,
} from '@/components/spotter/historicalDataTab/ChartWithInfo';
import {
    HydrophoneAudioPlayer,
    HydrophoneAudioPlayerHandle,
} from '@/components/spotter/historicalDataTab/HydrophoneAudioPlayer';
import { DataDetailSectionTimestamp } from '@/components/spotter/historicalDataTab/datumDetail/DataDetailSectionTimestamp';
import {
    SpectrogramBandReadingsDetail,
    SpectrogramReadingsSummary,
} from '@/components/spotter/historicalDataTab/datumDetail/SpectrogramDatumDetail';
import { useSpotterContext } from '@/context/SpotterContext';
import { useTimelineNavigationContext } from '@/context/TimelineNavigationContext';
import { useViewToken } from '@/context/ViewTokenContext';
import { useChartInteractionTracking } from '@/hooks/useChartInteractionTracking';
import { useElementSize } from '@/hooks/useElementSize';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { TrackChartViewWhenRendered } from '@/hooks/useIsViewedTracking';
import { DateRange } from '@/store';

const DETAILED_SPECTROGRAM_CHART_TITLES = {
    detections: 'Detailed spectrogram (detection)',
    spectrogram: 'Detailed spectrogram (sample)',
} as const;

export const ReadingSpectrogramChart = ({
    dateRange,
    sensorPosition,
    openedFrom,
    maxSpl,
    hideLoadingIndicator,
    onChartRendered,
}: {
    dateRange: DateRange;
    sensorPosition: string;
    openedFrom: 'detections' | 'spectrogram';
    maxSpl?: number;
    hideLoadingIndicator: boolean;
    onChartRendered?: (hasData: boolean, rendered?: { xTimeScale: d3.ScaleTime<number, number, never> }) => void;
}) => {
    const hydrophonePlaybackFlag = useFeatureFlag('hydrophonePlayback');
    const audioClipsFlag = useFeatureFlag('hydrophoneAudioClips');
    const audioLaneHeight = audioClipsFlag ? AUDIO_CLIP_LANE_HEIGHT_PX : 0;
    const chartHeightPx = 434 + audioLaneHeight;
    const playerRef = useRef<HydrophoneAudioPlayerHandle>(null);
    const timeZone = usePreferredTimeZone();
    const mixpanel = useMixpanel();
    const { spotterId } = useSpotterContext();
    const { loading: isViewTokenLoading, viewToken } = useViewToken();
    const { selectedSubsampleTimestamp, setSelectedSubsampleTimestamp } = useTimelineNavigationContext();
    const handleUserInteraction = useChartInteractionTracking('Select detailed spectrogram data', {
        spotterId,
        openedFrom,
    });
    const [selectedData, setSelectedData] = useState<SoundPressureReading | null>(null);
    /** @see the chart reference in `SampleSpectrogramChart` in `SpectrogramSection.tsx` for why this is not state. */
    const chartRef = useRef<Spectrogram<SoundPressureReading, SoundPressureReading['bands'][number]> | null>(null);

    /**
     * The cursor a freshly built chart starts from. Read through a reference so that moving the cursor does
     * not tear the chart down and rebuild it.
     */
    const selectedSubsampleTimestampRef = useRef(selectedSubsampleTimestamp);
    selectedSubsampleTimestampRef.current = selectedSubsampleTimestamp;

    const containerId = 'spectrogram-readings-chart' + useId();
    const containerRef = useRef<HTMLElement>(null);
    const { width } = useElementSize(containerRef);
    const drawChart = useCallback(drawSpectrogram, []);

    /**
     * we are still reassigning onChartRenderedRef.current on every component render so the onChartRendered is always the latest
     * but having it inside the ref is keeping the drawChart effect from being re-run on every render, which would destroy and re-create the chart unnecessarily
     */
    const onChartRenderedRef = useRef(onChartRendered);
    onChartRenderedRef.current = onChartRendered;

    const {
        data: allSensorData,
        isPending,
        isPlaceholderData,
    } = useSoundPressureReadings({
        spotterId,
        dateRange,
        fillGaps: true,
        includeBands: true,
    });

    const dataset = useMemo(() => {
        if (!allSensorData) {
            return null;
        }
        return allSensorData[sensorPosition] ?? allSensorData['sensor-null']; // workaround for data from before readings had sensor position attached
    }, [allSensorData, sensorPosition]);

    const allTimestamps = useMemo(() => map(dataset, 'timestamp'), [dataset]);

    const hasData = useMemo(() => some(dataset, (d) => d.bands.length), [dataset]);

    const selectTimestamp = useCallback(
        (timestamp: string | null) => {
            setSelectedSubsampleTimestamp(timestamp, allTimestamps);
            playerRef.current?.setPlaybackTimestamp(timestamp);
        },
        [setSelectedSubsampleTimestamp, allTimestamps]
    );

    // one detailed spectrogram shows continuous readings of one sensor, so they all come from one node
    const bristlemouthNodeId = find(dataset, 'bristlemouthNodeId')?.bristlemouthNodeId;

    const { data: audioClips } = useAudioClips({
        spotterId,
        bristlemouthNodeId,
        dateRange,
        config: { enabled: audioClipsFlag },
    });
    const startMs = dateRange.startDate.getTime();
    const endMs = dateRange.endDate.getTime();
    const audioClipsInRange = filter(
        audioClips,
        (clip) => new Date(clip.start_time).getTime() >= startMs && new Date(clip.end_time).getTime() <= endMs
    );
    const savableClips = filter(audioClipsInRange, { status: 'succeeded' });
    // clips do not overlap, so at most one clip contains the selected time
    const selectedClip = selectedSubsampleTimestamp
        ? find(savableClips, (clip) =>
              isWithinInterval(new Date(selectedSubsampleTimestamp), {
                  start: new Date(clip.start_time),
                  end: new Date(clip.end_time),
              })
          )
        : undefined;
    // the clip request is only for the device owner, so a view token leaves nothing to save
    const saveButtons = audioClipsFlag && spotterId && !viewToken && !isViewTokenLoading && (
        <AudioClipSaveButtons spotterId={spotterId} savableClips={savableClips} selectedClip={selectedClip} />
    );

    /** Where the last drawn chart put its plot, so the clip lane lines up with its axis. */
    const [chartLayout, setChartLayout] = useState<Pick<AudioClipLaneProps, 'xTimeScale' | 'margin' | 'height'>>();

    /*
     * Create `audio` once and keep it out of the JSX, so it is the same element in every render branch and
     * never `null`. The effect below can then attach its listeners once and pause it on unmount.
     */
    const [audio] = useState(() => new Audio());
    const [clipPlayback, setClipPlayback] = useState<ClipPlayback>(null);
    /** The clip whose URL `audio.src` holds. The media event handlers read it to know which clip the event is for. */
    const audioClipIdRef = useRef<number | null>(null);

    useEffect(() => {
        const setPhase = (phase: ClipPlaybackPhase) => () => {
            const clipId = audioClipIdRef.current;
            setClipPlayback(clipId === null ? null : { clipId, phase });
        };
        const handleLoading = setPhase('loading');
        const handlePlaying = setPhase('playing');
        const handleStop = () => setClipPlayback(null);
        // `play` fires before any audio data arrives, and `waiting` fires when the buffer runs out
        audio.addEventListener('play', handleLoading);
        audio.addEventListener('waiting', handleLoading);
        audio.addEventListener('playing', handlePlaying);
        audio.addEventListener('pause', handleStop);
        audio.addEventListener('ended', handleStop);
        audio.addEventListener('error', handleStop);

        return () => {
            audio.removeEventListener('play', handleLoading);
            audio.removeEventListener('waiting', handleLoading);
            audio.removeEventListener('playing', handlePlaying);
            audio.removeEventListener('pause', handleStop);
            audio.removeEventListener('ended', handleStop);
            audio.removeEventListener('error', handleStop);
            // the `pause` event of the call below reaches no listener, so reset `clipPlayback` here
            audio.pause();
            audio.removeAttribute('src');
            audioClipIdRef.current = null;
            setClipPlayback(null);
        };
    }, [audio, startMs, endMs, bristlemouthNodeId]);

    const handleToggleClip = (clip: AudioClip) => {
        if (!spotterId) {
            return;
        }

        if (clip.id === clipPlayback?.clipId) {
            audio.pause();
            return;
        }

        // select the reading's own `timestamp` string: the arrow keys find the selected time by string equality
        const clipStartMs = new Date(clip.start_time).getTime();
        const clipStartReading = find(dataset, (reading) => new Date(reading.timestamp).getTime() === clipStartMs);
        selectTimestamp(clipStartReading?.timestamp ?? clip.start_time);
        handleUserInteraction('click');
        mixpanel?.track('Audio clip playback started', {
            spotterId,
            bristlemouthNodeId: clip.bristlemouth_node_id,
            audioExfilId: clip.id,
        });

        if (clip.id !== audioClipIdRef.current) {
            audioClipIdRef.current = clip.id;
            audio.src = getAudioClipUrl(spotterId, clip.id, { download: false });
        }
        audio.play().catch(() => {
            // a switch to another clip also rejects this promise; reset only while `clip` is still the source
            if (audioClipIdRef.current === clip.id) {
                setClipPlayback(null);
            }
        });
    };

    /*
     * Deliberately free of fetch state: a background refetch that changes nothing must not tear the chart
     * down, so this asks only whether there is something to draw. The same value gates the container in the
     * JSX below, because the draw effect needs that container in the DOM.
     */
    const canDrawChart = hasData && !!dataset?.length;

    useEffect(() => {
        if (!canDrawChart || !dataset?.length) {
            return;
        }

        const newChart = drawChart<SoundPressureReading, SoundPressureReading['bands'][number]>({
            dataset,
            config: {
                id: 'spectrogram-readings',
                label: 'Frequency (Hz)',
                legend: { label: 'SPL (dB re 1 µPa²)' },
                valueKey: 'frequency' as const,
                colorKey: 'spl',
                maxColorValue: maxSpl,
                yAxisLabel: 'Frequency (Hz)',
                dataForBand: property('bands'),
            },
            containerId,
            chartWidth: width,
            chartHeight: chartHeightPx,
            chartMargin: { top: 8 },
            audioLaneHeight,
            showSeconds: true,
            timeZone,
            onChartRendered: (rendered) => {
                setChartLayout(rendered);
                onChartRenderedRef.current?.(true, rendered);
            },
            onSelectData: (selected) => {
                if (selected) {
                    setSelectedData(selected.band);
                } else {
                    setSelectedData(null);
                }
            },
            onSelectTimestamp: selectTimestamp,
            onUserInteraction: handleUserInteraction,
        });
        chartRef.current = newChart;
        // a chart built mid-session starts from the current cursor; the effect below owns every change after that
        newChart.setSelectedTimestamp(
            selectedSubsampleTimestampRef.current ? new Date(selectedSubsampleTimestampRef.current) : null
        );

        return () => {
            newChart.destroy();
            if (chartRef.current === newChart) {
                chartRef.current = null;
            }
        };
    }, [
        canDrawChart,
        dataset,
        drawChart,
        containerId,
        width,
        timeZone,
        selectTimestamp,
        handleUserInteraction,
        maxSpl,
        chartHeightPx,
        audioLaneHeight,
    ]);

    useEffect(() => {
        chartRef.current?.setSelectedTimestamp(
            selectedSubsampleTimestamp ? new Date(selectedSubsampleTimestamp) : null
        );
    }, [selectedSubsampleTimestamp]);

    /*
     * The parent uses this state to control the expansion bracket and sample chart legend. Keep it aligned with
     * whether the chart can be drawn; using refetch status would hide the bracket and reset the legend gradient
     * whenever the data is invalidated.
     */
    useEffect(() => {
        if (!canDrawChart) {
            onChartRenderedRef.current?.(false);
        }
    }, [canDrawChart]);

    if (!hasData && hideLoadingIndicator) {
        return false;
    }

    if (canDrawChart) {
        return (
            <>
                <TrackChartViewWhenRendered
                    containerEl={containerRef}
                    chartTitle={DETAILED_SPECTROGRAM_CHART_TITLES[openedFrom]}
                    spotterId={spotterId}
                    dateRange={dateRange}
                />
                <ChartWithInfo
                    chartHeight={`${chartHeightPx}px`}
                    detailPanelSx={{ height: `${354 + audioLaneHeight}px` }}
                    chart={
                        <Box width={1} height={1}>
                            {/*
                             * Anchor the lane on a zero-height box, not on a positioned wrapper. The chart tooltip
                             * sits above the plot, so its containing block must stay outside the clipped chart box.
                             */}
                            {audioClipsFlag && chartLayout && (
                                <Box data-testid="audio-clip-lane-anchor" position="relative" height={0}>
                                    <AudioClipLane
                                        {...chartLayout}
                                        clips={audioClipsInRange}
                                        timeZone={timeZone}
                                        clipPlayback={clipPlayback}
                                        onToggleClip={handleToggleClip}
                                    />
                                </Box>
                            )}
                            <Box ref={containerRef} id={containerId} width={1} height={1} />
                        </Box>
                    }
                >
                    <ChartDataDetails>
                        {!selectedData && <SpectrogramReadingsSummary dataset={dataset} actions={saveButtons} />}
                        {selectedData && (
                            <>
                                <DataDetailSectionTimestamp
                                    timestamp={selectedData.timestamp ?? null}
                                    processingSource={selectedData.bands.length ? 'hdr' : undefined}
                                    includeSeconds
                                />
                                {saveButtons}
                                <Box sx={{ m: 1 }}>
                                    <SpectrogramBandReadingsDetail data={selectedData} />
                                </Box>
                            </>
                        )}
                    </ChartDataDetails>
                    <ChartDataSelectors>
                        {dataset && hydrophonePlaybackFlag && (
                            <HydrophoneAudioPlayer ref={playerRef} stream={dataset} />
                        )}
                    </ChartDataSelectors>
                </ChartWithInfo>
            </>
        );
    }

    /*
     * Only these two states mean the readings we hold are not the ones asked for. A refetch of the window
     * already on screen is neither, and deliberately shows nothing: keeping the chart still is worth more
     * here than announcing a refresh that usually changes nothing outside the window.
     */
    if (isPending || isPlaceholderData) {
        return (
            <LoadingIndicatorIcon
                isLoading
                loadingMessage="Loading second-resolution sound pressure readings"
                loadingSx={{
                    my: 2,
                    mx: 'auto',
                    pr: CHART_DETAIL_PANEL_WIDTH,
                }}
            />
        );
    }

    return (
        <Alert
            severity="info"
            sx={{
                my: 2,
                mr: CHART_DETAIL_PANEL_WIDTH,
                justifyContent: 'center',
            }}
        >
            Second-resolution SPL readings not available for selected sample time
        </Alert>
    );
};
